import { FC, useMemo } from "react";
import { grey } from "@mui/material/colors";
import { ColDef } from "ag-grid-community";
import { useFlag } from "@unleash/proxy-client-react";
import { SavingsOpportunity } from "@/services/cloudchipr.api";
import { AgDataGrid } from "@/components/pages/common/ag-grid/AgDataGrid";
import { getSavingsOpportunitiesTableColumns } from "@/components/pages/savings-opportunities/components/savingsOpportunitiesTableColumns";
import { useAppSelector } from "@/store/hooks";
import { taskAvailableStatusPropertiesSelector } from "@/store/task-management/selectors/properties/taskAvailableStatusPropertiesSelector";
import { opportunitiesGridDefaultColumnDef } from "@/components/pages/common/opportunities/utils/constants/gridConstants";

interface ResourceDetailsOpportunityRowContentTableProps {
  opportunity: SavingsOpportunity;
}

export const ResourceDetailsOpportunityRowContentTable: FC<
  ResourceDetailsOpportunityRowContentTableProps
> = ({ opportunity }) => {
  const statuses = useAppSelector(taskAvailableStatusPropertiesSelector);
  const enableBillingCostAndForecastedEOMForOpportunities = useFlag(
    "EnableBillingCostAndForecastedEOMForOpportunities",
  );

  const columnDefs = useMemo(
    () =>
      normalizeColDefs(
        getSavingsOpportunitiesTableColumns(
          statuses,
          enableBillingCostAndForecastedEOMForOpportunities,
        ),
      ),
    [statuses, enableBillingCostAndForecastedEOMForOpportunities],
  );

  return (
    <AgDataGrid
      rowHeight={54}
      context={context}
      rowData={[opportunity]}
      columnDefs={columnDefs}
      rowGroupPanelShow="never"
      wrapperStyles={wrapperStyles}
      defaultColDef={opportunitiesGridDefaultColumnDef}
    />
  );
};

const wrapperStyles = {
  height: 120,
  borderRadius: 4,
  overflow: "hidden",
  border: `1px solid ${grey[300]}`,
};

const context = { hideCountInHeader: true };

const normalizeColDefs = (colDefs: ColDef<SavingsOpportunity>[]) => {
  return colDefs
    .filter((colDef) => {
      const colIds: (string | undefined)[] = [
        "opportunity_name",
        "action_type",
        "monthly_savings",
        "region",
        "cloud_provider",
        "implementation_effort",
        "account.provider_account_id",
      ];

      return !colIds.includes(colDef.colId) && !colIds.includes(colDef.field);
    })
    .map((colDef) => ({
      ...colDef,
      filter: false,
      pinned: false,
      sortable: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderContextMenu: true,
      suppressColumnsToolPanel: true,
      suppressHeaderMenuButton: true,
    }));
};
